import React, { useState, useEffect } from "react";
import { Modal, Form, Radio, Space, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { useNotification } from "../../shared/hooks";
import { updateUserRoles, RestaurantUser } from "./api";

const { Text } = Typography;

interface EditUserRolesModalProps {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  user: RestaurantUser | null;
  onSuccess: () => void;
}

export const EditUserRolesModal: React.FC<EditUserRolesModalProps> = ({
  open,
  onClose,
  restaurantId,
  user,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess, notifyError } = useNotification();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({ role: user.roles?.[0] || "" });
    }
  }, [open, user, form]);

  const handleSubmit = async () => {
    if (!user) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      updateUserRoles(
        restaurantId,
        user.user_id,
        { roles: values.role ? [values.role] : [] },
        () => {
          setLoading(false);
          showSuccess(t("roles_updated_successfully"));
          onSuccess();
        },
        (err) => {
          setLoading(false);
          notifyError(t("failed_to_update_roles"), err);
        }
      );
    } catch {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          {t("edit_roles")}
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={t("save")}
      cancelText={t("cancel")}
      confirmLoading={loading}
      destroyOnHidden
    >
      {user && (
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{t("editing_roles_for")}</Text>
          <Text strong style={{ marginLeft: 8 }}>
            {user.email}
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item name="role" label={t("role")}>
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="">{t("role_regular_user")}</Radio>
              <Radio value="Admin">{t("role_admin")}</Radio>
              <Radio value="Manager">{t("role_manager")}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>
      </Form>
    </Modal>
  );
};
